import { useMemo } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { IconButton, Switch } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Variable, VariableType } from "../../../models/interactions/Variable";
import NodeNumberField from "./NodeNumberField";
import PiInputfield from "../../../components/PiInputField";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import {
  getBuiltInFriendlyLabel,
  isBuiltInVariable,
} from "../../../models/interactions/BuiltInSequenceVariables";
interface TypedMatchInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  variableType: VariableType | null;
  variables: Variable[];
  excludeVariableId?: string;
  onDelete?: () => void;
  canDelete?: boolean;
  allowVariableSelection?: boolean;
  showVariableDropdown?: boolean;
}

const isCompatibleType = (variableType: VariableType, targetType: VariableType) => {
  if (targetType === VariableType.String || targetType === VariableType.BuiltIn) {
    return (
      variableType === VariableType.String ||
      variableType === VariableType.BuiltIn
    );
  }
  return variableType === targetType;
};

export default function TypedMatchInput({
  label,
  value,
  onChange,
  variableType,
  variables,
  excludeVariableId,
  onDelete,
  canDelete = true,
  allowVariableSelection = true,
  showVariableDropdown = true,
}: TypedMatchInputProps) {
  const effectiveVariables = useMemo(
    () => (allowVariableSelection ? variables : []),
    [allowVariableSelection, variables],
  );

  const matchedVariable = useMemo(
    () => effectiveVariables.find((v) => v.id === value),
    [effectiveVariables, value],
  );
  const isVariableReference = matchedVariable != null;

  const filteredVariables = useMemo(() => {
    if (!variableType) return [];
    return effectiveVariables.filter(
      (v) =>
        isCompatibleType(v.type, variableType) &&
        v.id !== excludeVariableId &&
        v.id !== value,
    );
  }, [effectiveVariables, variableType, excludeVariableId, value]);

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const hasVariableOptions =
    filteredVariables.length > 0 || isVariableReference;
  const hasDeleteOption = Boolean(onDelete);
  const hasDropdownContent = hasVariableOptions || hasDeleteOption;
  const dropdownDisabled = !showVariableDropdown || !hasDropdownContent;

  const VariableDropdownButton = () => (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild onClick={(e) => e.stopPropagation()}>
        <IconButton
          size="small"
          className="!p-0 !min-w-0"
          aria-label="Select variable"
          disabled={dropdownDisabled}
        >
          <CalculateOutlinedIcon
            className={
              !dropdownDisabled
                ? "text-creator-primary"
                : "cursor-not-allowed text-creator-primary-disabled"
            }
          />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className="flex flex-col max-h-[200px] pit-creator-scrollbar overflow-y-auto bg-creator-surface overflow-x-hidden rounded-[10px] shadow-lg z-40"
          style={{
            padding: "6px",
            minWidth: "160px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
          }}
          onWheel={handleWheel}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sideOffset={5}
        >
          {filteredVariables.map((variable) => (
            <DropdownMenu.Item
              key={variable.id}
              className="rounded-[5px] transition-colors cursor-pointer focus:outline-none"
              onWheel={handleWheel}
              onSelect={() => {
                onChange(variable.id);
              }}
            >
              <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-creator-primary-background rounded-[5px]">
                <DataObjectIcon
                  sx={{ fontSize: 20 }}
                  className="text-creator-primary"
                />
                <span className="text-[14px] font-medium text-creator-text-sub font-poppins">
                  {isBuiltInVariable(variable)
                    ? `${getBuiltInFriendlyLabel(variable.name)} (${variable.name})`
                    : variable.name}
                </span>
              </div>
            </DropdownMenu.Item>
          ))}
          {isVariableReference && (
            <DropdownMenu.Item
              className="rounded-md transition-colors cursor-pointer focus:outline-none"
              onWheel={handleWheel}
              onSelect={() => {
                onChange("");
              }}
            >
              <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-creator-primary-background rounded-[5px]">
                <CalculateOutlinedIcon
                  sx={{ fontSize: 20 }}
                  className="text-creator-primary"
                />
                <span className="text-[14px] font-medium text-creator-text-sub font-poppins">
                  Enter value
                </span>
              </div>
            </DropdownMenu.Item>
          )}
          {hasDeleteOption && hasVariableOptions && (
            <DropdownMenu.Separator className="my-1 h-px bg-creator-border" />
          )}
          {hasDeleteOption && (
            <DropdownMenu.Item
              className="rounded-[5px] transition-colors focus:outline-none"
              disabled={!canDelete}
              onSelect={(event) => {
                if (!canDelete) {
                  event.preventDefault();
                  return;
                }
                onDelete?.();
              }}
            >
              <div
                className={`rounded-[5px] flex items-center gap-3 px-3 py-2.5 ${
                  canDelete
                    ? "cursor-pointer hover:bg-creator-primary-background"
                    : "cursor-not-allowed opacity-60"
                }`}
              >
                <DeleteOutlineOutlinedIcon
                  sx={{ fontSize: 20 }}
                  className={
                    canDelete ? "text-creator-error" : "text-creator-error"
                  }
                />
                <span className="text-[14px] font-medium text-creator-error font-poppins">
                  Delete
                </span>
              </div>
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const renderVariableDropdown = () =>
    showVariableDropdown ? <VariableDropdownButton /> : null;

  if (variableType === null) {
    return (
      <div className="flex flex-col gap-[8px] noDrag nowheel">
        <div className="font-medium text-creator-text-sub text-[14px]">{label}</div>
        <div className="flex flex-row gap-2 items-center">
          <div className="flex-1 min-w-0">
            <PiInputfield
              label=""
              placeholder="Select a variable first"
              value=""
              onChange={() => {}}
              size="compact"
              disabled
            />
          </div>
          {renderVariableDropdown()}
        </div>
      </div>
    );
  }

  const inputContainerClass =
    "flex w-full bg-creator-surface border border-solid items-center border-creator-border h-[36px] rounded-[6px] px-[10px] gap-[10px] min-w-0 transition-[border-color,box-shadow] duration-150 hover:border-creator-text-soft focus-within:border-creator-primary focus-within:shadow-[0_0_0_3px_rgba(58,196,255,0.15)]";

  if (isVariableReference) {
    return (
      <div className="flex flex-col gap-[8px] noDrag nowheel">
        <div className="font-medium text-creator-text-sub text-[14px]">{label}</div>
        <div className="flex flex-row gap-2 items-center">
          <div
            className={`flex-1 truncate ${inputContainerClass} text-creator-text-sub text-[12px]`}
          >
            {isBuiltInVariable(matchedVariable)
              ? `${getBuiltInFriendlyLabel(matchedVariable.name)} (${matchedVariable.name})`
              : matchedVariable.name}
          </div>
          {renderVariableDropdown()}
        </div>
      </div>
    );
  }

  const renderInput = () => {
    switch (variableType) {
      case VariableType.Boolean: {
        const checked = value === "true";
        return (
          <div className="flex w-full h-[32px] items-center rounded-full bg-creator-surface min-w-0">
            <div className=" translate-x-[-12px]">
              <Switch
                checked={checked}
                onChange={(_, checked) => onChange(checked ? "true" : "false")}
                sx={{ margin: 0 }}
              />
            </div>
          </div>
        );
      }
      case VariableType.Number: {
        const numValue =
          value === "" || isNaN(Number(value)) ? 0 : Number(value);
        return (
          <NodeNumberField
            label=""
            value={numValue}
            onChange={(num) => onChange(String(num))}
          />
        );
      }
      case VariableType.String:
      case VariableType.BuiltIn:
      default:
        return (
          <PiInputfield
            label=""
            placeholder="Text"
            value={value}
            onChange={onChange}
            size="compact"
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-[8px] noDrag nowheel">
      <div className="font-medium text-creator-text-sub text-[14px]">{label}</div>
      <div className="flex flex-row gap-2 items-center">
        <div className="flex-1 min-w-0">{renderInput()}</div>
        {renderVariableDropdown()}
      </div>
    </div>
  );
}
